import React from 'react';
import { Menu } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircleXmark, faMobileScreen, faReceipt } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { resolveTip } from 'shared-models/browser';
import type { PaymentTerminalView } from '../../../../../services/credit-payments/credit-payments.service';
import { formatMoney } from '../../../../../utils/money';
import type { PaymentlineView } from '../../../../../services/payments/payments.service';
import orderClasses from '../section/order-detail.module.scss';
import menuClasses from './payment-method-menu.module.scss';

// A tapped payment method + its binding; the amount keypad opens before any pay/charge.
export type PaymentMethodTap =
  | { kind: 'cash' }
  | { kind: 'pinpad'; terminalId: number; pinpadId: string }
  | { kind: 'manual'; terminalId: number; pinpadId: string }
  | { kind: 'top' };

type PaymentActionsRowProps = {
  paymentline: PaymentlineView;
  globalTipShare: number;
  isMarkingPaymentDone: boolean;
  isSplittingPayment: boolean;
  isSettingTip: boolean;
  isLocked: boolean;
  isFailed?: boolean;
  terminals: PaymentTerminalView[];
  isTapOnPhoneAvailable?: boolean;
  // e2e sandbox tenant: manual credit stays available on refund lines (hidden for real tenants).
  tenantIsTest?: boolean;
  manualCreditEntryBlocked?: boolean;
  allowElectronicPayment?: boolean;
  showPartialReceipt?: boolean;
  onMethodTap: (method: PaymentMethodTap) => void;
  onTip: () => void;
  onSplit: () => void;
  onCancelPayment?: () => void;
  onClearFailure?: () => void;
  onPartialReceipt?: () => void;
};

type QuickPaymentMethodsProps = Pick<
  PaymentActionsRowProps,
  'paymentline' | 'isMarkingPaymentDone' | 'isSplittingPayment' | 'isSettingTip' | 'isLocked' | 'terminals' | 'isTapOnPhoneAvailable' | 'tenantIsTest' | 'manualCreditEntryBlocked' | 'allowElectronicPayment' | 'onMethodTap'
>;

export const QuickPaymentMethods: React.FC<QuickPaymentMethodsProps> = ({
  paymentline,
  isMarkingPaymentDone,
  isSplittingPayment,
  isSettingTip,
  isLocked,
  terminals,
  isTapOnPhoneAvailable = false,
  tenantIsTest = false,
  manualCreditEntryBlocked = false,
  allowElectronicPayment = true,
  onMethodTap,
}) => {
  const isDisabled = isMarkingPaymentDone || isSplittingPayment || isSettingTip || isLocked || paymentline.status !== 'new';
  const isRefundLine = paymentline.amount < 0;
  const isManualCreditAllowed = (!isRefundLine || tenantIsTest) && !manualCreditEntryBlocked;
  const manualCreditTarget = terminals.find((terminal) => terminal.pinpads.length > 0);

  return (
    <section className={orderClasses.quickPaymentMethods} aria-label="תשלומים מהירים">
      <h2>תשלומים מהירים</h2>
      <div className={orderClasses.quickPaymentMethodsList}>
        <button type="button" data-testid="payment-method-grid-cash" disabled={isDisabled} onClick={() => onMethodTap({ kind: 'cash' })}>
          <img src="/money.png" alt="" />
          <span>מזומן</span>
        </button>
        {!isRefundLine && isTapOnPhoneAvailable && allowElectronicPayment && (
          <button type="button" data-testid="payment-method-grid-tap" disabled={isDisabled} onClick={() => onMethodTap({ kind: 'top' })}>
            <FontAwesomeIcon icon={faMobileScreen as IconProp} style={{color:'black'}}/>
            <span>מכשיר</span>
          </button>
        )}
        {allowElectronicPayment && terminals.flatMap((terminal) =>
          terminal.pinpads.map((pinpad) => (
            <button
              key={`${terminal.id}:${pinpad.pinpadId}`}
              type="button"
              data-testid={`payment-method-grid-pinpad-${pinpad.pinpadId}`}
              disabled={isDisabled}
              onClick={() => onMethodTap({ kind: 'pinpad', terminalId: terminal.id, pinpadId: pinpad.pinpadId })}
            >
              <img src="/card-bank.png" alt="" />
              <span>{pinpad.label ?? `${terminal.name} ${pinpad.pinpadId}`}</span>
            </button>
          )),
        )}
        {isManualCreditAllowed && allowElectronicPayment && manualCreditTarget && (
          <button
            type="button"
            data-testid="payment-method-grid-manual"
            disabled={isDisabled}
            onClick={() => onMethodTap({
              kind: 'manual',
              terminalId: manualCreditTarget.id,
              pinpadId: manualCreditTarget.pinpads[0].pinpadId,
            })}
          >
            <img src="/card-bank.png" alt="" />
            <span>אשראי ידני</span>
          </button>
        )}
      </div>
    </section>
  );
};

export const PaymentActionsRow: React.FC<PaymentActionsRowProps> = ({
  paymentline,
  globalTipShare,
  isMarkingPaymentDone,
  isSplittingPayment,
  isSettingTip,
  isLocked,
  isFailed = false,
  terminals,
  isTapOnPhoneAvailable = false,
  tenantIsTest = false,
  manualCreditEntryBlocked = false,
  allowElectronicPayment = true,
  showPartialReceipt = false,
  onMethodTap,
  onTip,
  onSplit,
  onCancelPayment,
  onClearFailure,
  onPartialReceipt,
}) => {
  const isDone = paymentline.status === 'done';
  const isPaymentMutationInFlight = isMarkingPaymentDone || isSplittingPayment || isSettingTip || isLocked;
  const isProcessingThisCreditPayment = isLocked;
  const hasTerminals = terminals.length > 0;
  // The pay menu opens when there is any electronic option: configured terminals or the on-device tap reader.
  const hasPayMenu = allowElectronicPayment && (hasTerminals || isTapOnPhoneAvailable);
  // Manual credit is a single entry; bind it to the first active pinpad (the cashier types the card, not the terminal).
  const firstTerminal = terminals.find((terminal) => terminal.pinpads.length > 0);
  const manualCreditTarget = firstTerminal
    ? { terminalId: firstTerminal.id, pinpadId: firstTerminal.pinpads[0].pinpadId }
    : null;
  const isRefundLine = paymentline.amount < 0;
  const isManualCreditAllowed = (!isRefundLine || tenantIsTest) && !manualCreditEntryBlocked;
  const lineTip = resolveTip(paymentline.tipInputMode ?? null, paymentline.tipInputValue ?? null, paymentline.amount);
  const hasTip = lineTip > 0;
  const tipTotal = lineTip + globalTipShare;
  const totalCharge = paymentline.amount + tipTotal;

  const amountBlock = (
    <div className={orderClasses.paymentAmountBlock}>
      <span className={orderClasses.paymentAmountLabel}>סכום לחיוב</span>
      <span className={orderClasses.paymentAmountValue}>{formatMoney(totalCharge)} ₪</span>
      {tipTotal > 0 && (
        <span className={orderClasses.paymentTipNote}><span dir="ltr">{formatMoney(tipTotal)} ₪</span> :טיפ</span>
      )}
    </div>
  );

  const tipTile = (
    <button
      type="button"
      data-testid="payment-tip-open"
      className={`${orderClasses.paymentTileButton} ${orderClasses.paymentTipButton} ${hasTip ? orderClasses.paymentTipButtonOn : ''}`}
      disabled={isPaymentMutationInFlight || paymentline.status !== 'new'}
      onClick={onTip}
    >
      <img src="/tips.png" alt="" className={orderClasses.paymentTileImage} />
      <span className={orderClasses.paymentTileTitle}>{hasTip ? `טיפ · ${formatMoney(lineTip)}` : 'טיפ'}</span>
    </button>
  );

  const failedTipTile = (
    <button
      type="button"
      data-testid="payment-tip-open"
      className={`${orderClasses.paymentTileButton} ${orderClasses.paymentTipButton} ${hasTip ? orderClasses.paymentTipButtonOn : ''}`}
      disabled
      onClick={onTip}
    >
      <img src="/tips.png" alt="" className={orderClasses.paymentTileImage} />
      <span className={orderClasses.paymentTileTitle}>{hasTip ? `טיפ · ${formatMoney(lineTip)}` : 'טיפ'}</span>
    </button>
  );

  if (isDone) {
    return (
      <div
        className={`${orderClasses.paymentLine} ${orderClasses.paymentLineDone}`}
        data-testid="payment-line"
        data-status="done"
      >
        {amountBlock}
        <div className={orderClasses.paymentDonePanel}>
          <span className={orderClasses.paymentDoneIcon}>
            <FontAwesomeIcon icon={faCircleCheck as IconProp} />
          </span>
          <span className={orderClasses.paymentDoneDesktopLabel}>תשלום עבר בהצלחה</span>
          <span className={orderClasses.paymentDoneMobileLabel}> שולם </span>
          {paymentline.method === 'cash' && (
            <button
              type="button"
              data-testid="payment-cancel-cash"
              className={orderClasses.paymentCancelCashButton}
              disabled={isPaymentMutationInFlight || !onCancelPayment}
              onClick={() => onCancelPayment?.()}
            >
              <span className={orderClasses.paymentCancelCashButtonDesktopLabel}>בטל תשלום</span>
              <span className={orderClasses.paymentCancelCashButtonMobileLabel}>בטל</span>
            </button>
          )}
        </div>
        {tipTile}
        {showPartialReceipt ? (
          <button
            type="button"
            data-testid="payment-partial-receipt"
            className={`${orderClasses.paymentTileButton} ${orderClasses.paymentSplitButton}`}
            onClick={onPartialReceipt}
          >
            <FontAwesomeIcon icon={faReceipt as IconProp} className={orderClasses.paymentTileImage} />
            <span className={orderClasses.paymentTileTitle}>קבלה חלקית</span>
          </button>
        ) : (
          <button
            type="button"
            className={`${orderClasses.paymentTileButton} ${orderClasses.paymentSplitButton}`}
            disabled
          >
            <img src="/splitPayments.png" alt="פיצול" className={orderClasses.paymentTileImage} />
            <span className={orderClasses.paymentTileTitle}>פיצול</span>
          </button>
        )}
      </div>
    );
  }

  if (isFailed) {
    return (
      <div
        className={`${orderClasses.paymentLine} ${orderClasses.paymentLineFailed}`}
        data-testid="payment-line"
        data-status="failed"
      >
        {amountBlock}
        <div className={`${orderClasses.paymentDonePanel} ${orderClasses.paymentDonePanelFailed}`}>
          <span className={`${orderClasses.paymentDoneIcon} ${orderClasses.paymentDoneIconFailed}`}>
            <FontAwesomeIcon icon={faCircleXmark as IconProp} />
          </span>
          <span className={`${orderClasses.paymentDoneDesktopLabel} ${orderClasses.paymentDoneLabelFailed}`}>התשלום נכשל</span>
          <span className={`${orderClasses.paymentDoneMobileLabel} ${orderClasses.paymentDoneLabelFailed}`}>נכשל</span>
          <button
            type="button"
            data-testid="payment-clear-failure"
            className={orderClasses.paymentCancelCashButton}
            disabled={isPaymentMutationInFlight || !onClearFailure}
            onClick={() => onClearFailure?.()}
          >
            <span className={orderClasses.paymentCancelCashButtonDesktopLabel}>אימות</span>
            <span className={orderClasses.paymentCancelCashButtonMobileLabel}>אימות</span>
          </button>
        </div>
        {failedTipTile}
        <button
          type="button"
          className={`${orderClasses.paymentTileButton} ${orderClasses.paymentSplitButton}`}
          data-testid="payment-split-open"
          disabled
          onClick={onSplit}
        >
          <img src="/splitPayments.png" alt="פיצול" className={orderClasses.paymentTileImage} />
          <span className={orderClasses.paymentTileTitle}>פיצול</span>
        </button>
      </div>
    );
  }

  return (
    <div className={orderClasses.paymentLine} data-testid="payment-line" data-status="new">
      {amountBlock}
      {hasPayMenu ? (
        <Menu position="bottom" withinPortal shadow="md" disabled={isPaymentMutationInFlight || paymentline.status !== 'new'}>
          <Menu.Target>
            <button
              type="button"
              data-testid="payment-pay-button"
              className={`${orderClasses.paymentTileButton} ${orderClasses.paymentPayButton}`}
              disabled={isPaymentMutationInFlight || paymentline.status !== 'new'}
            >
              <img src="/pay.png" alt="שלם" className={orderClasses.paymentTileImage} />
              <span className={orderClasses.paymentTileTitle}>{isProcessingThisCreditPayment ? 'מעבד...' : 'הזן סכום'}</span>
            </button>
          </Menu.Target>
          <Menu.Dropdown className={menuClasses.dropdown}>
            <Menu.Item
              className={menuClasses.item}
              data-testid="payment-method-cash"
              onClick={() => onMethodTap({ kind: 'cash' })}
            >
              <span className={menuClasses.itemBody}>
                <img src="/money.png" alt="" className={menuClasses.itemIcon} />
                <span className={menuClasses.itemLabel}>מזומן</span>
              </span>
            </Menu.Item>
            {!isRefundLine && isTapOnPhoneAvailable && (
              <Menu.Item
                className={menuClasses.item}
                data-testid="payment-method-tap"
                onClick={() => onMethodTap({ kind: 'top' })}
              >
                <span className={menuClasses.itemBody}>
                  <FontAwesomeIcon icon={faMobileScreen as IconProp} className={menuClasses.itemIconPhone} />
                  <span className={menuClasses.itemLabel}>מכשיר</span>
                </span>
              </Menu.Item>
            )}
            {terminals.flatMap((terminal) =>
              terminal.pinpads.map((pinpad) => (
                <Menu.Item
                  key={`${terminal.id}:${pinpad.pinpadId}`}
                  className={menuClasses.item}
                  data-testid="payment-method-pinpad"
                  onClick={() => onMethodTap({ kind: 'pinpad', terminalId: terminal.id, pinpadId: pinpad.pinpadId })}
                >
                  <span className={menuClasses.itemBody}>
                    <img src="/card-bank.png" alt="" className={menuClasses.itemIcon} />
                    <span className={menuClasses.itemLabel}>{pinpad.label ?? `${terminal.name} ${pinpad.pinpadId}`}</span>
                  </span>
                </Menu.Item>
              )),
            )}
            {isManualCreditAllowed && manualCreditTarget && (
              <Menu.Item
                className={menuClasses.item}
                data-testid="payment-method-manual"
                onClick={() => onMethodTap({ kind: 'manual', terminalId: manualCreditTarget.terminalId, pinpadId: manualCreditTarget.pinpadId })}
              >
                <span className={menuClasses.itemBody}>
                  <img src="/card-bank.png" alt="" className={menuClasses.itemIcon} />
                  <span className={menuClasses.itemLabel}>אשראי ידני</span>
                </span>
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>
      ) : (
        <button
          type="button"
          data-testid="payment-pay-button"
          className={`${orderClasses.paymentTileButton} ${orderClasses.paymentPayButton}`}
          disabled={isPaymentMutationInFlight || paymentline.status !== 'new'}
          onClick={() => onMethodTap({ kind: 'cash' })}
        >
          <img src="/pay.png" alt="שלם" className={orderClasses.paymentTileImage} />
          <span className={orderClasses.paymentTileTitle}>{isProcessingThisCreditPayment ? 'מעבד...' : 'הזן סכום'}</span>
        </button>
      )}
      {tipTile}
      <button
        type="button"
        className={`${orderClasses.paymentTileButton} ${orderClasses.paymentSplitButton}`}
        data-testid="payment-split-open"
        disabled={isPaymentMutationInFlight || paymentline.status !== 'new'}
        onClick={onSplit}
      >
        <img src="/splitPayments.png" alt="פיצול" className={orderClasses.paymentTileImage} />
        <span className={orderClasses.paymentTileTitle}>פיצול</span>
      </button>
    </div>
  );
};
